import { checkDecorators } from "./checkDecorators";
import { checkMatchChildPath } from "./checkMatchChildPath";
import { checkMatchPath } from "./checkMatchPath";
import { checkPowerPlacement } from "./checkPowerPlacement";
import { checkSemanticType } from "./checkSemanticType";
import { checkType } from "./checkType";
import { checkUnit } from "./checkUnit";
import { getNegativeCondition } from "./helpers";
import { NEGATIVE_MATCH, POSITIVE_MATCH, DEFAULT_MATCH, NO_MATCH } from "./types";

import type { NodeConditionOptions } from "./types";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeCondition } from "@corca/data";

function checkNodeChildType(nodeCondition: NodeCondition, node: NodeModel): number {
  if (!nodeCondition.hasChildType) {
    return NO_MATCH;
  }

  const positiveConditions = nodeCondition.hasChildType.filter((c) => !getNegativeCondition(c));
  const hasPositiveMatch = positiveConditions.some((condition) => {
    return node.nodes.some((childNode) => {
      return checkNodeCondition({ hasType: [condition] }, childNode);
    });
  });

  if (hasPositiveMatch) {
    return POSITIVE_MATCH;
  }

  const negativeConditions = nodeCondition.hasChildType
    .map((c) => getNegativeCondition(c))
    .filter(Boolean);
  const hasNegativeMatch = negativeConditions.some((condition) => {
    return node.nodes.some((childNode) => {
      return checkNodeCondition({ hasType: [condition] } as NodeCondition, childNode);
    });
  });

  if (hasNegativeMatch) {
    return NEGATIVE_MATCH;
  }

  if (positiveConditions.length === 0 && negativeConditions.length > 0) {
    return DEFAULT_MATCH;
  }

  if (positiveConditions.length > 0) {
    return NEGATIVE_MATCH;
  }

  return NO_MATCH;
}

export function checkNodeCondition(
  nodeCondition?: NodeCondition | "none" | "present",
  node?: NodeModel,
  { skipDecoratorsCondition }: NodeConditionOptions = {},
) {
  if (nodeCondition === undefined) return true;
  if (nodeCondition === "none") return !node;
  if (nodeCondition === "present") return !!node;

  if (!node) return false;

  const semanticTypeCheck = checkSemanticType(nodeCondition, node);
  if (semanticTypeCheck === NEGATIVE_MATCH) return false;
  const decoratorsCheck = checkDecorators(nodeCondition, node, { skipDecoratorsCondition });
  if (decoratorsCheck === NEGATIVE_MATCH) return false;
  const powerPlacementCheck = checkPowerPlacement(nodeCondition.hasPowerPlacement, node);
  if (powerPlacementCheck === NEGATIVE_MATCH) return false;
  const unitCheck = checkUnit(nodeCondition, node);
  if (unitCheck === NEGATIVE_MATCH) return false;
  const childTypeCheck = checkNodeChildType(nodeCondition, node);
  if (childTypeCheck === NEGATIVE_MATCH) return false;
  const matchPathCheck = checkMatchPath(nodeCondition, node);
  if (matchPathCheck === NEGATIVE_MATCH) return false;
  const matchChildPathCheck = checkMatchChildPath(nodeCondition, node);
  if (matchChildPathCheck === NEGATIVE_MATCH) return false;
  const typeCheck = checkType(nodeCondition, node);
  if (typeCheck === NEGATIVE_MATCH) return false;

  if (
    semanticTypeCheck === POSITIVE_MATCH ||
    decoratorsCheck === POSITIVE_MATCH ||
    powerPlacementCheck === POSITIVE_MATCH ||
    unitCheck === POSITIVE_MATCH ||
    childTypeCheck === POSITIVE_MATCH ||
    matchPathCheck === POSITIVE_MATCH ||
    matchChildPathCheck === POSITIVE_MATCH ||
    typeCheck === POSITIVE_MATCH
  ) {
    return true;
  }

  if (
    semanticTypeCheck === DEFAULT_MATCH ||
    decoratorsCheck === DEFAULT_MATCH ||
    powerPlacementCheck === DEFAULT_MATCH ||
    unitCheck === DEFAULT_MATCH ||
    childTypeCheck === DEFAULT_MATCH ||
    matchPathCheck === DEFAULT_MATCH ||
    matchChildPathCheck === DEFAULT_MATCH ||
    typeCheck === DEFAULT_MATCH
  ) {
    return true;
  }

  return false;
}
